import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api';
import { questsApi } from '../quests/api';
import type { Quest, QuestAssignmentWithUser } from '../quests/types';
import { QuestForm, isoToDateInput, type QuestFormBody } from './QuestForm';

export function QuestDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  // When this page mounts, invalidate the list query so when the user
  // clicks the back arrow (← Quests) the list refetches and shows the
  // latest data. Without this, react-query serves the stale cached
  // list and the user sees the pre-edit state.
  useEffect(() => {
    qc.invalidateQueries({ queryKey: ['admin-quests'] });
  }, [qc, id]);
  const quest = useQuery({
    queryKey: ['admin-quest', id],
    queryFn: () => questsApi.get(id),
    enabled: !!id,
  });

  const cancel = useMutation({
    mutationFn: () => questsApi.cancel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-quest', id] });
      navigate('/admin/quests');
    },
  });
  const archive = useMutation({
    mutationFn: () => questsApi.archive(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-quest', id] });
      qc.invalidateQueries({ queryKey: ['admin-quests'] });
      navigate('/admin/quests');
    },
  });
  const restore = useMutation({
    mutationFn: () => questsApi.restore(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-quest', id] }),
  });
  const confirmCompletion = useMutation({
    // M5.10: pass the attempt so the admin's confirm
    // targets the right (user, attempt) row. For non-
    // repeatable quests, attempt defaults to 1 in the
    // route.
    mutationFn: ({ userId, attempt }: { userId: string; attempt: number }) =>
      questsApi.confirmCompletion(id, userId, attempt),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-quest', id] }),
  });
  // Fetch the competency list once so we can render the full name
  // (e.g. "Coding & Debugging") instead of the raw id
  // (comp_coding_debugging) in the Details card. 60s staleTime
  // because competencies change rarely.
  const competencies = useQuery({
    queryKey: ['competencies'],
    queryFn: () => api<{ competencies: Array<{ id: string; name: string }> }>('/api/competencies'),
    staleTime: 60_000,
  });
  const update = useMutation({
    mutationFn: (body: Parameters<typeof questsApi.update>[1]) => questsApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-quest', id] });
      qc.invalidateQueries({ queryKey: ['admin-quests'] });
    },
  });

  const [editing, setEditing] = useState(false);

  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  if (quest.isLoading) return <p className="text-ink-muted">Loading…</p>;
  if (!quest.data) return <p className="text-accent-danger">Failed to load quest.</p>;
  const q: Quest = quest.data.quest;
  const assignments: QuestAssignmentWithUser[] = quest.data.assignments ?? [];

  const pendingReview = assignments.filter((a) => a.status === 'pending_review');
  const claimed = assignments.filter((a) => a.status === 'claimed');
  const completed = assignments.filter((a) => a.status === 'completed');
  const available = assignments.filter((a) => a.status === 'available');

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <Link to="/admin/quests" className="text-sm text-ink-muted hover:text-ink">← Quests</Link>
          <h1 className="mt-1 text-2xl font-semibold">{q.title}</h1>
          <p className="text-ink-muted">
            {q.mode === 'open' ? 'Open — first come' : 'Assigned to specific users'}
            {/* M5.15: split total XP + the per-competency
                breakdown in the subtitle. Empty splits
                (a ceremonial / recognition quest) shows
                "no XP" instead of "0 XP" so it doesn't
                read as a typo. Same for coins. */}
            {q.splits.length > 0 ? (
              <>
                · {q.splits.reduce((sum, s) => sum + s.xp, 0)} XP across {q.splits.length} {q.splits.length === 1 ? 'competency' : 'competencies'}
                {q.splits.some((s) => (s.coins ?? 0) > 0) ? (
                  <> · {q.splits.reduce((sum, s) => sum + (s.coins ?? 0), 0)} 🪙</>
                ) : null}
              </>
            ) : (
              <> · no XP</>
            )}
            {q.review_mode === 'review' ? ' · review required' : ''}
            {q.repeatable ? ' · repeatable' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          {/* v0.8.9: edit the quest definition (title, description,
              competency rewards, mode, assignees…) after creation.
              Hidden for terminal states (cancelled / archived) —
              restore first, then edit. */}
          {!editing && q.status !== 'cancelled' && q.status !== 'archived' ? (
            <button className="btn" onClick={() => { setEditing(true); setFeedback(null); }}>Edit</button>
          ) : null}
          {q.status === 'open' && !editing ? (
            <button className="btn" onClick={() => update.mutate({ status: 'paused' })}>Pause</button>
          ) : null}
          {q.status === 'paused' && !editing ? (
            <button className="btn" onClick={() => update.mutate({ status: 'open' })}>Resume</button>
          ) : null}
          {q.status === 'open' ? (
            !confirmArchive ? (
              <button className="btn" onClick={() => setConfirmArchive(true)}>Archive</button>
            ) : (
              <div className="flex gap-2">
                <button
                  className="btn"
                  onClick={() => archive.mutate()}
                  disabled={archive.isPending}
                >Confirm archive</button>
                <button className="btn" onClick={() => setConfirmArchive(false)}>Keep</button>
              </div>
            )
          ) : null}
          {q.status === 'cancelled' || q.status === 'archived' ? (
            <button
              className="btn"
              onClick={async () => {
                if (!confirm(`Restore "${q.title}" to open?`)) return;
                try {
                  await restore.mutateAsync();
                  setFeedback({ kind: 'ok', text: 'Restored. Players can claim again.' });
                } catch (e) {
                  setFeedback({ kind: 'err', text: e instanceof ApiError ? e.message : 'Restore failed.' });
                }
              }}
            >
              Restore
            </button>
          ) : null}
          {q.status !== 'cancelled' && q.status !== 'archived' ? (
            !confirmCancel ? (
              <button className="btn-danger" onClick={() => setConfirmCancel(true)}>Cancel</button>
            ) : (
              <div className="flex gap-2">
                <button
                  className="btn-danger"
                  onClick={() => cancel.mutate()}
                  disabled={cancel.isPending}
                >Confirm cancel</button>
                <button className="btn" onClick={() => setConfirmCancel(false)}>Keep</button>
              </div>
            )
          ) : null}
        </div>
      </header>

      {feedback ? (
        <div
          className={feedback.kind === 'ok' ? 'card border-l-4 border-l-accent-success text-sm' : 'card border-l-4 border-l-accent-danger text-sm'}
          role="status"
        >
          {feedback.text}
          <button className="btn ml-3" onClick={() => setFeedback(null)}>Dismiss</button>
        </div>
      ) : null}

      {editing ? (
        <QuestForm
          initial={{
            title: q.title,
            description: q.description ?? '',
            event_type: q.event_type,
            splits: q.splits.map((s) => ({
              key: `s_${s.competency_id}`,
              competency_id: s.competency_id,
              xp: s.xp,
              coins: s.coins ?? 0,
            })),
            mode: q.mode,
            max_completions: q.max_completions,
            review_mode: q.review_mode,
            repeatable: q.repeatable,
            due_at: isoToDateInput(q.due_at),
            // Preselect everyone still on the quest. The api's
            // assign_to sync only cancels untouched 'available'
            // rows, so keeping completed/claimed users checked
            // is safe (their rows are never touched).
            assign_to: assignments
              .filter((a) => a.status !== 'cancelled')
              .map((a) => a.user_id),
          }}
          submitLabel="Save changes"
          submitting={update.isPending}
          error={null}
          cancelHref={`/admin/quests/${id}`}
          onSubmit={(body: QuestFormBody) => {
            setFeedback(null);
            update.mutate(
              {
                title: body.title,
                description: body.description,
                event_type: body.event_type,
                splits: body.splits,
                mode: body.mode,
                max_completions: body.max_completions,
                review_mode: body.review_mode,
                repeatable: body.repeatable,
                due_at: body.due_at,
                assign_to: body.assign_to,
              },
              {
                onSuccess: () => {
                  setEditing(false);
                  setFeedback({ kind: 'ok', text: 'Quest updated.' });
                },
                onError: (e) => setFeedback({ kind: 'err', text: e instanceof ApiError ? e.message : 'Update failed.' }),
              },
            );
          }}
        />
      ) : (
      <div className="card">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Details</h2>
        <p className="mt-1 whitespace-pre-wrap text-sm text-ink-muted">{q.description || 'No description.'}</p>
        <dl className="mt-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
          <Field label="Status" value={q.status} accent={
            q.status === 'cancelled' ? 'danger'
            : q.status === 'archived' ? 'dim'
            : q.status === 'completed' ? 'success'
            : q.status === 'paused' ? 'warning'
            : 'normal'
          } />
          <Field label="Mode" value={q.mode} />
          <Field label="Max completions" value={String(q.max_completions)} />
          <Field label="Event type" value={q.event_type} mono />
          {/*
            M5.15: replace the single-competency field
            with a per-split breakdown. The split rows
            show the competency name (resolved via the
            competencies query), the XP, the coins (when
            non-zero), and the total XP + total coins at
            the bottom. The competency dropdown inside the
            editor (QuestNewPage) ensures no duplicate
            competencies can be picked; this list is
            read-only and surfaces the same shape the api
            stored.
          */}
          <div className="col-span-2 space-y-1 sm:col-span-4">
            <dt className="text-xs text-ink-dim">Competency rewards</dt>
            <dd>
              {q.splits.length === 0 ? (
                <span className="text-sm text-ink-dim">No rewards configured.</span>
              ) : (
                <ul className="space-y-1 text-sm">
                  {q.splits.map((s, i) => (
                    <li key={i} className="flex items-baseline gap-2">
                      <span className="font-medium">
                        {(competencies.data?.competencies ?? []).find((c: { id: string; name: string }) => c.id === s.competency_id)?.name ?? s.competency_id}
                      </span>
                      <span className="text-xs text-ink-dim">·</span>
                      <span className="font-mono text-accent-success">+{s.xp} XP</span>
                      {(s.coins ?? 0) > 0 ? (
                        <>
                          <span className="text-xs text-ink-dim">·</span>
                          <span className="font-mono text-accent-warning">+{s.coins} 🪙</span>
                        </>
                      ) : null}
                    </li>
                  ))}
                  {q.splits.some((s) => (s.coins ?? 0) > 0) ? (
                    <li className="mt-1 flex items-baseline gap-2 border-t border-white/10 pt-1 text-xs text-ink-muted">
                      <span className="font-medium">Total</span>
                      <span>·</span>
                      <span className="font-mono">{q.splits.reduce((sum, s) => sum + s.xp, 0)} XP</span>
                      <span>·</span>
                      <span className="font-mono">{q.splits.reduce((sum, s) => sum + (s.coins ?? 0), 0)} 🪙</span>
                    </li>
                  ) : (
                    <li className="mt-1 flex items-baseline gap-2 border-t border-white/10 pt-1 text-xs text-ink-muted">
                      <span className="font-medium">Total</span>
                      <span>·</span>
                      <span className="font-mono">{q.splits.reduce((sum, s) => sum + s.xp, 0)} XP</span>
                    </li>
                  )}
                </ul>
              )}
            </dd>
          </div>
          {q.due_at ? <Field label="Due" value={new Date(q.due_at).toLocaleString()} /> : null}
        </dl>
      </div>
      )}

      {pendingReview.length > 0 ? (
        <div className="card border-l-4 border-l-accent-warning">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
            Awaiting your review ({pendingReview.length})
          </h2>
          <ul className="mt-2 space-y-2">
            {pendingReview.map((a) => (
              <li key={`${a.user_id}-${a.attempt}`} className="flex items-center justify-between rounded-md bg-bg-elevated p-2">
                <div>
                  <p className="font-medium">
                    {a.user_display_name} <span className="text-xs text-ink-dim">@{a.user_handle}</span>
                    {/* M5.10: show the attempt number for
                        repeatable quests. For non-
                        repeatable quests, attempt is
                        always 1 and we hide the badge
                        to keep the row clean. */}
                    {q.repeatable ? (
                      <span className="ml-2 rounded-full bg-bg-panel px-2 py-0.5 text-xs text-ink-muted">
                        attempt {a.attempt}
                      </span>
                    ) : null}
                  </p>
                  <p className="text-xs text-ink-muted">Claimed {a.claimed_at ? new Date(a.claimed_at).toLocaleString() : '—'}</p>
                </div>
                <button
                  className="btn-primary"
                  onClick={async () => {
                    setFeedback(null);
                    try {
                      const r = await confirmCompletion.mutateAsync({ userId: a.user_id, attempt: a.attempt });
                      // M5.15: confirm response now carries
                      // total_xp + total_coins + splits. We
                      // show the totals in the success toast
                      // and the per-competency breakdown in
                      // the assignment list below (if the
                      // admin wants to see the detail). The
                      // toast stays short on purpose — the
                      // detail page has the rest.
                      const xpPart = `${r.total_xp} XP`;
                      const coinPart = r.total_coins > 0 ? ` + ${r.total_coins} 🪙` : '';
                      setFeedback({ kind: 'ok', text: `Confirmed attempt ${a.attempt}. ${xpPart}${coinPart} awarded across ${r.splits.length} ${r.splits.length === 1 ? 'competency' : 'competencies'}.` });
                    } catch (e) {
                      setFeedback({ kind: 'err', text: e instanceof ApiError ? e.message : 'Confirm failed.' });
                    }
                  }}
                  disabled={confirmCompletion.isPending}
                >
                  Confirm & award
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <AssignmentGroup title="Claimed" assignments={claimed} />
      <AssignmentGroup title="Available" assignments={available} />
      <AssignmentGroup title="Completed" assignments={completed} />
    </div>
  );
}

function Field({ label, value, mono, accent }: { label: string; value: string; mono?: boolean; accent?: 'normal' | 'success' | 'danger' | 'warning' | 'dim' }) {
  const cls = (() => {
    switch (accent) {
      case 'success': return 'text-accent-success';
      case 'danger':  return 'text-accent-danger';
      case 'warning': return 'text-accent-warning';
      case 'dim':     return 'text-ink-dim';
      default:        return '';
    }
  })();
  return (
    <div>
      <dt className="text-xs text-ink-dim">{label}</dt>
      <dd className={`${mono ? 'font-mono text-sm' : 'text-sm'} ${cls}`}>{value}</dd>
    </div>
  );
}

function AssignmentGroup({ title, assignments }: { title: string; assignments: QuestAssignmentWithUser[] }) {
  if (assignments.length === 0) return null;
  return (
    <div className="card">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">{title} ({assignments.length})</h2>
      <ul className="mt-2 space-y-1 text-sm">
        {assignments.map((a) => (
          <li key={a.user_id} className="flex items-baseline justify-between rounded-md bg-bg-elevated px-2 py-1">
            <span>
              {a.user_display_name} <span className="text-xs text-ink-dim">@{a.user_handle}</span>
            </span>
            <span className="text-xs text-ink-dim">
              {assignmentTimeline(a)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function assignmentTimeline(a: QuestAssignmentWithUser): string {
  const parts: string[] = [a.status];
  if (a.claimed_at) parts.push(`claimed ${new Date(a.claimed_at).toLocaleDateString()}`);
  if (a.completed_at) parts.push(`done ${new Date(a.completed_at).toLocaleDateString()}`);
  return parts.join(' · ');
}
